"use client";
import { forwardRef, useCallback, useState } from "react"
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"
import {
  Select,
  MenuItem,
  Divider,
  Box,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"

const FONT_SIZES = [
  { label: "10px", value: 10 },
  { label: "12px", value: 12 },
  { label: "14px", value: 14 },
  { label: "16px", value: 16 },
  { label: "18px", value: 18 },
  { label: "20px", value: 20 },
  { label: "24px", value: 24 },
  { label: "28px", value: 28 },
  { label: "32px", value: 32 },
  { label: "36px", value: 36 },
]

export const FontSizePopover = forwardRef((
  {
    editor: providedEditor,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [customSize, setCustomSize] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectOpen, setSelectOpen] = useState(false)

  const currentSize = editor?.getAttributes('textStyle')?.fontSize || null

  const handleSizeChange = useCallback((event) => {
    if (!editor) return
    const selectedValue = event.target.value
    
    if (selectedValue) {
      const size = parseInt(selectedValue)
      editor.chain().focus().setFontSize(size).run()
      setSelectOpen(false)
    } else {
      editor.chain().focus().unsetFontSize().run()
      setSelectOpen(false)
    }
  }, [editor])

  const handleCustomSize = useCallback(() => {
    if (!editor || !customSize) return
    const size = parseInt(customSize)
    if (size > 0 && size <= 200) {
      editor.chain().focus().setFontSize(size).run()
      setCustomSize("")
      setSelectOpen(false)
    }
  }, [editor, customSize])

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    setSelectOpen(true)
  }

  const handleClose = () => {
    setSelectOpen(false)
    setAnchorEl(null)
  }

  return (
    <Select
      ref={ref}
      value={currentSize || ""}
      onChange={handleSizeChange}
      onOpen={handleOpen}
      onClose={handleClose}
      open={selectOpen}
      displayEmpty
      size="small"
      renderValue={(selected) => {
        if (!selected) return <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Aa</span>
        return `${selected}px`
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 400,
            '& .MuiMenuItem-root': {
              minHeight: 36,
            }
          }
        },
        onClose: handleClose,
      }}
      sx={{
        height: '32px',
        fontSize: '14px',
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
      {...buttonProps}
    >
      <MenuItem disabled sx={{ py: 0, minHeight: 'auto' }}>
        <Divider sx={{ width: '100%' }} />
      </MenuItem>
      <MenuItem 
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        sx={{ py: 1, pointerEvents: 'none' }}
      >
        <Box 
          sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pointerEvents: 'auto' }} 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            type="number"
            placeholder="크기 입력 (px)"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation()
                e.preventDefault()
                handleCustomSize()
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            inputProps={{
              min: 1,
              max: 200,
            }}
            sx={{ flex: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      handleCustomSize()
                    }}
                    disabled={!customSize}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </MenuItem>
      <MenuItem disabled sx={{ py: 0, minHeight: 'auto' }}>
        <Divider sx={{ width: '100%' }} />
      </MenuItem>
      {FONT_SIZES.map((size) => (
        <MenuItem
          key={size.value}
          value={size.value}
          selected={currentSize === size.value}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ fontSize: `${size.value}px` }}>{size.label}</span>
            {currentSize === size.value && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
})

FontSizePopover.displayName = "FontSizePopover"

export default FontSizePopover
